import { Fragment, Ref, useCallback, useMemo } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import TitledTextArea from "@coworker/apprestructured/src/task-management/components/TitledTextArea/TitledTextArea";
import crossCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossCircleIconPath";
import styles from "./FailedCriteriaCreatorFragment.module.css";
import { useTranslation } from "react-i18next";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUser } from "@coworker/apprestructured/src/shared/hooks/users/useUser";
import { getSchemaForTaskPath } from "@coworker/apprestructured/src/shared/helpers/task.helper";
import type {
  FailedCriteriaItem,
  FailedCriteriaComment,
} from "@coworker/apprestructured/src/task-management/types/TestBuy";

export type FailedCriteriaCreatorFragmentConfigProps = {
  field: string;
};

type FailedCriteriaCreatorFragmentProps = {
  configProps: FailedCriteriaCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

type FailedCriteriaItemEntry = {
  item: FailedCriteriaItem;
  value: string;
  error: string | null;
};

function FailedCriteriaItem({
  entry,
  onChange,
  disabled,
}: {
  entry: FailedCriteriaItemEntry;
  onChange: (newValue: string, entry: FailedCriteriaItemEntry) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { item, value, error } = entry;

  return (
    <div className={styles["criteria-item"]}>
      <div className={styles["criteria-header"]}>
        <FixaSSRIcon paths={crossCircleIconPath} />
        <FixaText className={styles["criteria--dark"]}>
          {t(`tb${item.type}`)}
        </FixaText>
      </div>

      <TitledTextArea
        label={t("addCommentString")}
        value={value}
        rows={1}
        onChange={(newValue) => onChange(newValue, entry)}
        disabled={disabled}
        error={error}
      />
    </div>
  );
}

function FailedCriteriaCreatorFragment({
  configProps,
  commonProps,
}: FailedCriteriaCreatorFragmentProps) {
  const { t } = useTranslation();
  const myUserId = useUID();
  const myFullName = useUser(myUserId).data?.fullName;
  const { task, taskType, onTaskChange, blockInput } = commonProps;
  const { field } = configProps;

  const maxCommentLength =
    getSchemaForTaskPath(taskType, `${field}.comments.text`)?.maxLength || 1000;

  const criteriaData = getByPath(task, field) as
    | FailedCriteriaItem[]
    | undefined;
  const criteriaValues = useMemo(() => {
    return criteriaData?.map((item) => {
      const value = item.comments?.[0]?.text ?? "";
      const error =
        value.length > maxCommentLength
          ? t("maximumCharactersLengthString", { count: maxCommentLength })
          : null;
      return { item, value, error };
    });
  }, [criteriaData, maxCommentLength, t]);

  const isLoadingValues = criteriaValues?.some((item) => isLoading(item.value));

  const handleChangeEntry = useCallback(
    (newValue: string, entry: FailedCriteriaItemEntry) => {
      if (
        newValue.length > maxCommentLength &&
        newValue.length >= entry.value.length
      ) {
        return;
      }

      const updatedComments: FailedCriteriaComment[] =
        newValue.trim().length === 0
          ? []
          : [
              {
                initial_comment: true,
                creator_user_id: myUserId,
                title: myFullName ?? "",
                text: newValue,
                created_at: new Date().toISOString(),
              },
            ];

      const updatedCriteria = (criteriaData ?? []).map((criteria) =>
        criteria.type === entry.item.type
          ? { ...criteria, comments: updatedComments }
          : criteria
      );

      onTaskChange(setByPath(field, updatedCriteria));
    },
    [criteriaData, field, myUserId, myFullName, onTaskChange, maxCommentLength]
  );

  if (isLoadingValues) {
    return <LoadingCreatorFragment />;
  }

  if (!criteriaValues || criteriaValues.length === 0) {
    return null;
  }

  return (
    <div className={styles["wrapper"]}>
      {criteriaValues.map((entry) => (
        <Fragment key={entry.item.type}>
          <FailedCriteriaItem
            entry={entry}
            onChange={handleChangeEntry}
            disabled={blockInput}
          />
        </Fragment>
      ))}
    </div>
  );
}

export default FailedCriteriaCreatorFragment;
